import { lazy, Suspense, useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppShell } from './components/app-shell';
import { LearnFrame } from './components/learn-frame';
import { ImpersonationBanner } from './components/impersonation-banner';
import { ScrollToTop } from './components/scroll-to-top';
import { Loading } from './components/primitives';
import { getPublicConfig, restoreSession } from './lib/api';
import { useAuthStore } from './lib/auth-store';
import { useProfile, useWalled } from './lib/queries';
import { captureRef } from './lib/ref';
import { AchievePage } from './pages/achieve';
import { MoodPage } from './pages/achieve-mood';
import { ActionsPage } from './pages/actions';
import { AffirmationsPage } from './pages/affirmations';
import { AlignPage } from './pages/align';
import { AuraPage } from './pages/align-aura';
import { PalmistryPage } from './pages/align-palmistry';
import { KarmaPage } from './pages/align-karma';
import { AlignLearnPage } from './pages/align-learn';
import { MantraSessionPage, MantrasPage } from './pages/align-mantras';
import { MeditationSessionPage, MeditationsPage } from './pages/align-meditations';
import { BiorhythmPage } from './pages/align-biorhythm';
import { BreathworkPage, BreathworkSessionPage } from './pages/align-breathwork';
import { FrequenciesPage, FrequencyTrackPage } from './pages/align-frequencies';
import { BlueprintPage } from './pages/blueprint';
import { ChangePasswordPage } from './pages/change-password';
import { ChartPage } from './pages/chart';
import { CoachPage } from './pages/coach';
import { CoachPromptsPage } from './pages/coach-prompts';
import { CreditsPage } from './pages/credits';
import { InvitePage } from './pages/invite';
import { ProgramsPage } from './pages/programs';
import { LibraryItemPage, LibraryPage } from './pages/library';
import { ProgramPage } from './pages/program';
import { ProgramDayPage } from './pages/program-day';
import { GoalDetailPage } from './pages/goal-detail';
import { GoalsPage } from './pages/goals';
import { HomePage } from './pages/home';
import { CartPage } from './pages/cart';
import { ShopPage } from './pages/shop';
import { ShopProductPage } from './pages/shop-product';
import { ShopOrdersPage } from './pages/shop-orders';
import { LearnPage } from './pages/learn';
import { LearnBrowsePage } from './pages/learn-browse';
import { CourseDetailPage } from './pages/course-detail';
import { WalletPage } from './pages/wallet';
import { CashOutPage } from './pages/cash-out';
import { NetworkPage } from './pages/network';
import { VaultPage } from './pages/vault';
import { VaultRedeemPage } from './pages/vault-redeem';
import { WalletTopupPage } from './pages/wallet-topup';
import { LegalPage } from './pages/legal';
// The course player pulls in the whole shared VideoPlayer (adapters, control
// bar, captions). Loading that on first paint quadrupled the bundle, which is
// parse time every cold start on a cheap Android, so it is split out and paid
// for only by a learner who actually opens a lesson.
const LearnCoursePage = lazy(() =>
  import('./pages/learn-course').then((m) => ({ default: m.LearnCoursePage })),
);
import { ImpersonatePage } from './pages/impersonate';
import { JournalPage } from './pages/journal';
import { LoginPage } from './pages/login';
import { ManifestPage } from './pages/manifest';
import { ThreeSixNinePage } from './pages/manifest-369';
import { VisionBoardPage } from './pages/manifest-vision-board';
import { FutureSelfPage } from './pages/manifest-future-self';
import { AlignmentTodosPage } from './pages/manifest-todos';
import { ToolkitPage, ToolkitSessionPage } from './pages/manifest-toolkit';
import { MiracleWinsPage } from './pages/manifest-wins';
import { OnboardingPage } from './pages/onboarding';
import { ProfilePage } from './pages/profile';
import { EveningReviewPage } from './pages/review-evening';
import { WeeklyReviewPage } from './pages/review-weekly';
import { MorningRitualPage } from './pages/ritual-morning';
import { ScorePage } from './pages/score';
import { SettingsPage } from './pages/settings';
import { NotificationSettingsPage } from './pages/settings-notifications';
import { SignupPage } from './pages/signup';
import { VedicPage } from './pages/vedic';
import { WelcomePage } from './pages/welcome';
import { PaywallPage } from './pages/paywall';
import { UpgradePage } from './pages/upgrade';
import { OrderResultPage } from './pages/order-result';

export function App() {
  const token = useAuthStore((s) => s.token);
  const [booting, setBooting] = useState(true);
  const config = useQuery({
    queryKey: ['public-config'],
    queryFn: getPublicConfig,
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    captureRef(new URLSearchParams(window.location.search).get('ref'));
    if (useAuthStore.getState().token) {
      setBooting(false);
      return;
    }
    void restoreSession().finally(() => setBooting(false));
  }, []);

  if (booting || config.isPending) return <Splash />;
  if (config.isError || !config.data.coachingEnabled) return <NotAvailable />;

  return (
    <>
      <ScrollToTop />
      <ImpersonationBanner />
      <Routes>
        <Route
          path="/"
          element={<Navigate to={token ? '/home' : `/welcome${window.location.search}`} replace />}
        />
        <Route element={<PublicOnly />}>
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage config={config.data} />} />
        </Route>
        {/* Public and un-gated: an app store reviewer and a signed-out member
            must both be able to read these, and the apex serves this app, so
            https://<domain>/privacy is the canonical url for the listing. */}
        <Route path="/privacy" element={<LegalPage kind="privacy" />} />
        <Route path="/terms" element={<LegalPage kind="terms" />} />
        <Route path="/impersonate" element={<ImpersonatePage />} />
        {/* Browsing and a course's page: open to a visitor while the tenant
            shows its courses publicly (`lmsPublicCatalog`, enforced by the API
            too). */}
        <Route element={<LearnArea publicCatalog={config.data.lmsPublicCatalog} />}>
          <Route path="/learn/browse" element={<LearnBrowsePage />} />
          <Route path="/learn/c/:productId" element={<CourseDetailPage />} />
        </Route>
        <Route element={<RequireAuth />}>
          <Route path="/change-password" element={<ChangePasswordPage />} />
          <Route element={<RequirePasswordChange />}>
            {/* My courses and the player are a member's own, but never behind
                the coaching onboarding: someone who joined to buy a course gets
                to it straight away. */}
            <Route element={<MemberLayout area="learn" />}>
              <Route path="/learn" element={<LearnPage />} />
              {/* Stripe's billing page returns members to /my-courses, the
                  website's name for this screen. */}
              <Route path="/my-courses" element={<Navigate to="/learn" replace />} />
              <Route
                path="/learn/:courseId"
                element={
                  <Suspense fallback={<Loading label="Loading the player" />}>
                    <LearnCoursePage />
                  </Suspense>
                }
              />
            </Route>
            {/* The store side of the portal (Shop, Orders, Wallet, Network,
                Codes) is a member's own as well, and never waits for the
                coaching onboarding: a member who updates the app keeps their
                wallet, slot and codes whether or not they set up coaching.
                Inside the app shell once onboarded, in the slim frame before. */}
            <Route element={<MemberLayout area="shop" />}>
              <Route path="/shop" element={<ShopPage />} />
              <Route path="/shop/orders" element={<ShopOrdersPage />} />
              <Route path="/shop/:id" element={<ShopProductPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/wallet" element={<WalletPage />} />
              <Route path="/wallet/cash-out" element={<CashOutPage />} />
              <Route path="/wallet/top-up" element={<WalletTopupPage />} />
              <Route path="/network" element={<NetworkPage />} />
              <Route path="/vault" element={<VaultPage />} />
              <Route path="/vault/redeem" element={<VaultRedeemPage />} />
            </Route>
            {/* Buying a plan sits OUTSIDE the onboarding guard: a member who
                taps Unlock on the paywall has not finished onboarding yet, and
                would otherwise be bounced back to it mid-purchase. */}
            <Route path="/upgrade" element={<UpgradePage />} />
            <Route path="/orders/:id" element={<OrderResultPage />} />
            <Route path="/credits" element={<CreditsPage />} />
            <Route element={<RequireOnboarding />}>
              <Route element={<RequireSubscription />}>
                <Route path="/onboarding/:step?" element={<OnboardingPage />} />
                <Route path="/paywall" element={<PaywallPage />} />
                <Route path="/blueprint" element={<BlueprintPage />} />
                <Route path="/ritual/morning" element={<MorningRitualPage />} />
                <Route path="/review/evening" element={<EveningReviewPage />} />
                {/* Full-screen tools: the blueprint hides the tab bar during a
                  session, a listening session and a review. */}
                <Route path="/review/weekly" element={<WeeklyReviewPage />} />
                <Route path="/align/breathwork/:id" element={<BreathworkSessionPage />} />
                <Route path="/align/frequencies/:id" element={<FrequencyTrackPage />} />
                <Route path="/align/mantras/:id" element={<MantraSessionPage />} />
                <Route path="/align/meditations/:id" element={<MeditationSessionPage />} />
                <Route path="/manifest/toolkit/:id" element={<ToolkitSessionPage />} />
                <Route element={<AppShell />}>
                  <Route path="/home" element={<HomePage />} />
                  {/* Declared beside the thread route: a static segment outranks the
                    optional param, so /coach/prompts never reads as a conversation id. */}
                  <Route path="/coach/prompts" element={<CoachPromptsPage />} />
                  <Route path="/coach/:conversationId?" element={<CoachPage />} />
                  <Route path="/manifest" element={<ManifestPage />} />
                  <Route path="/manifest/journal" element={<JournalPage />} />
                  <Route path="/manifest/affirmations" element={<AffirmationsPage />} />
                  <Route path="/manifest/369" element={<ThreeSixNinePage />} />
                  <Route path="/manifest/vision-board" element={<VisionBoardPage />} />
                  <Route path="/manifest/wins" element={<MiracleWinsPage />} />
                  <Route path="/manifest/toolkit" element={<ToolkitPage />} />
                  <Route path="/manifest/todos" element={<AlignmentTodosPage />} />
                  <Route path="/manifest/future-self" element={<FutureSelfPage />} />
                  <Route path="/align" element={<AlignPage />} />
                  <Route path="/align/vedic" element={<VedicPage />} />
                  <Route path="/align/learn" element={<AlignLearnPage />} />
                  <Route path="/align/karma" element={<KarmaPage />} />
                  <Route path="/align/aura" element={<AuraPage />} />
                  <Route path="/align/palmistry" element={<PalmistryPage />} />
                  <Route path="/align/chart" element={<ChartPage />} />
                  <Route path="/align/breathwork" element={<BreathworkPage />} />
                  <Route path="/align/frequencies" element={<FrequenciesPage />} />
                  <Route path="/align/biorhythm" element={<BiorhythmPage />} />
                  <Route path="/align/mantras" element={<MantrasPage />} />
                  <Route path="/align/meditations" element={<MeditationsPage />} />
                  <Route path="/achieve" element={<AchievePage />} />
                  <Route path="/library" element={<LibraryPage />} />
                  <Route path="/library/:id" element={<LibraryItemPage />} />
                  <Route path="/programs" element={<ProgramsPage />} />
                  <Route path="/programs/:idOrKey" element={<ProgramPage />} />
                  <Route path="/programs/:id/days/:dayNo" element={<ProgramDayPage />} />
                  <Route path="/achieve/goals" element={<GoalsPage />} />
                  <Route path="/achieve/goals/:id" element={<GoalDetailPage />} />
                  <Route path="/achieve/actions" element={<ActionsPage />} />
                  <Route path="/achieve/score" element={<ScorePage />} />
                  <Route path="/achieve/mood" element={<MoodPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/invite" element={<InvitePage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/settings/notifications" element={<NotificationSettingsPage />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

/** SC-01: brand, tagline, a quiet loading line. */
function Splash() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
      <p className="font-display text-3xl font-semibold text-ink">Miracles Protocol</p>
      <p className="eyebrow mt-3">Manifest. Align. Act. Succeed.</p>
      <p className="eyebrow mt-10 animate-pulse" role="status">
        Loading
      </p>
    </div>
  );
}

function NotAvailable() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center">
      <p className="font-display text-3xl font-semibold text-ink">Miracles Protocol</p>
      <p className="mt-3 max-w-sm text-sm leading-relaxed text-muted-foreground">
        The coach app is not available right now. Please check back soon.
      </p>
    </div>
  );
}

function PublicOnly() {
  const token = useAuthStore((s) => s.token);
  return token ? <Navigate to="/home" replace /> : <Outlet />;
}

function RequireAuth() {
  const token = useAuthStore((s) => s.token);
  const location = useLocation();
  return token ? (
    <Outlet />
  ) : (
    <Navigate to="/welcome" replace state={{ from: location.pathname }} />
  );
}

function RequirePasswordChange() {
  const user = useAuthStore((s) => s.user);
  return user?.mustChangePassword ? <Navigate to="/change-password" replace /> : <Outlet />;
}

/**
 * Browse and a course's page. A visitor sees them in the slim frame when the
 * tenant shows its courses publicly, and is sent to the welcome screen
 * otherwise; a member gets the ordinary Learn layout.
 */
function LearnArea({ publicCatalog }: { publicCatalog: boolean }) {
  const token = useAuthStore((s) => s.token);
  const location = useLocation();
  if (token) return <MemberLayout area="learn" />;
  return publicCatalog ? (
    <LearnFrame />
  ) : (
    <Navigate to="/welcome" replace state={{ from: location.pathname }} />
  );
}

/**
 * A member's Learn and store-side screens: inside the app shell once their
 * coaching is set up, and in the slim frame before that (the shell's coaching
 * tabs would all bounce them to the onboarding, while Shop and Learn work).
 * Only ever rendered with a token.
 */
function MemberLayout({ area }: { area: 'learn' | 'shop' }) {
  const user = useAuthStore((s) => s.user);
  const profile = useProfile();
  if (user?.mustChangePassword) return <Navigate to="/change-password" replace />;
  if (profile.isPending) return <Loading />;
  return profile.data?.profile.onboardingCompletedAt ? (
    <AppShell />
  ) : (
    <LearnFrame member area={area} />
  );
}

/**
 * No free tier (`paid_only`): once onboarding is done, a member with no live
 * plan sees the paywall and nothing else of the coaching app. The store side
 * (Shop, Learn, orders, wallet) is routed outside this guard and stays open,
 * and so do the screens that pay: the paywall, Upgrade, Credits, the order
 * result, Profile (to sign out) and the legal pages.
 */
const WALL_OPEN = [
  '/paywall',
  '/upgrade',
  '/credits',
  '/orders',
  '/profile',
  '/settings',
  '/change-password',
  '/legal',
  '/privacy',
  '/terms',
];
function RequireSubscription() {
  const walled = useWalled();
  const profile = useProfile();
  const { pathname } = useLocation();
  if (walled === null || profile.isPending) return <Loading />;
  const onboarded = Boolean(profile.data?.profile.onboardingCompletedAt);
  const open = WALL_OPEN.some((p) => pathname === p || pathname.startsWith(`${p}/`));
  if (walled && onboarded && !open) return <Navigate to="/paywall" replace />;
  return <Outlet />;
}

/** Onboarding is the front door until it is finished, and closed once it is. */
function RequireOnboarding() {
  const profile = useProfile();
  const { pathname } = useLocation();
  if (profile.isPending) return <Loading />;
  const completed = Boolean(profile.data?.profile.onboardingCompletedAt);
  const inOnboarding = pathname.startsWith('/onboarding');
  if (!completed && !inOnboarding && pathname !== '/blueprint' && pathname !== '/paywall')
    return <Navigate to="/onboarding" replace />;
  if (completed && inOnboarding) return <Navigate to="/home" replace />;
  return <Outlet />;
}
